import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import appAPI from '../../api/api'
import { Event } from '../../api/model/event'

export type EventsState = {
  events: Event[]
  calendarEvents: Event[]
  bannerOnMainPage?: Event
  page: number
  pages: number
  perPage: number
  loading: boolean
  calendarEventsLoading: boolean
  bannerOnMainPageLoading: boolean
}

export type GetEventsPayload = {
  limit?: number
  offset?: number
  periods?: 'past' | 'coming' | 'now'
  date_from?: string
  date_to?: string
  bannerOnMainPage?: boolean
}

const initialEventsState: EventsState = {
  events: [],
  calendarEvents: [],
  bannerOnMainPage: undefined,
  page: 0,
  pages: 1,
  perPage: 10,
  loading: false,
  calendarEventsLoading: false,
  bannerOnMainPageLoading: false,
}

const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async (payload: GetEventsPayload = { limit: 10, offset: 0 }) => {
    return await appAPI.events.getEvents(payload)
  },
)

const fetchCalendarEvents = createAsyncThunk(
  'events/fetchCalendarEvents',
  async ({ date_from, date_to }: Pick<GetEventsPayload, 'date_from' | 'date_to'>) => {
    const limit = 100

    const firstResponse = await appAPI.events.getEvents({
      limit,
      offset: 0,
      ...(date_from ? { date_from } : {}),
      ...(date_to ? { date_to } : {}),
    })

    const allEvents: Event[] = [...(firstResponse.data.results || [])]
    const pages = firstResponse.data.pages || 1

    for (let pageIndex = 1; pageIndex < pages; pageIndex += 1) {
      const response = await appAPI.events.getEvents({
        limit,
        offset: pageIndex * limit,
        ...(date_from ? { date_from } : {}),
        ...(date_to ? { date_to } : {}),
      })

      allEvents.push(...(response.data.results || []))
    }

    return allEvents
  },
)

const fetchEventBannerOnMainPage = createAsyncThunk('events/fetchEventBannerOnMainPage', async () => {
  const response = await appAPI.events.getEvents({ bannerOnMainPage: true })

  if (response.data.results && response.data?.results[0]) {
    await new Promise((resolve) => {
      const image = new Image()
      image.addEventListener('load', () => {
        resolve(true)
      })
      response.data.results && (image.src = response.data.results[0].mainImageThumb550x280)
    })
  }

  return response
})

const eventsSlice = createSlice({
  name: 'events',
  initialState: initialEventsState,
  reducers: {
    setPage: (state, { payload }: PayloadAction<number>) => {
      state.page = payload
    },
    clearEvents: (state) => {
      state.events = []
      state.calendarEvents = []
      state.bannerOnMainPage = undefined
      state.page = 0
      state.pages = 1
      state.perPage = 10
      state.loading = false
      state.calendarEventsLoading = false
      state.bannerOnMainPageLoading = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchEvents.fulfilled, (state, { payload, meta }) => {
        if ((meta.arg?.offset || 0) > 0) {
          state.events = [...state.events, ...(payload.data.results || [])]
        } else {
          state.events = payload.data.results || []
        }

        state.pages = payload.data.pages || 1
        state.perPage = meta.arg?.limit || 10
        state.loading = false
      })
      .addCase(fetchEvents.rejected, (state) => {
        state.loading = false
      })

      .addCase(fetchCalendarEvents.pending, (state) => {
        state.calendarEventsLoading = true
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, { payload }) => {
        state.calendarEvents = payload
        state.calendarEventsLoading = false
      })
      .addCase(fetchCalendarEvents.rejected, (state) => {
        state.calendarEventsLoading = false
      })

      .addCase(fetchEventBannerOnMainPage.pending, (state) => {
        state.bannerOnMainPageLoading = true
      })
      .addCase(fetchEventBannerOnMainPage.fulfilled, (state, { payload }) => {
        state.bannerOnMainPage = payload.data.results && payload.data.results[0]
        state.bannerOnMainPageLoading = false
      })
      .addCase(fetchEventBannerOnMainPage.rejected, (state) => {
        state.bannerOnMainPageLoading = false
      })
  },
})

export default eventsSlice.reducer

export const eventsSliceActions = {
  ...eventsSlice.actions,
  fetchEvents,
  fetchCalendarEvents,
  fetchEventBannerOnMainPage,
}
